import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { ColumnProps } from 'antd/lib/table';

import {
  ProcessedTrend,
  SeverityMixBar,
  TrendLegend,
} from '@totem/components/services/threats/eventsDashboard/report/charts';
import EventsReportContext from '@totem/components/services/threats/eventsDashboard/report/EventsReportContext';
import {
  buildingStatus,
  bySeverity,
} from '@totem/components/services/threats/eventsDashboard/report/SummaryTab';
import { DashboardBuilding } from '@totem/components/services/threats/eventsDashboard/report/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';

import './eventsReport.css';

const Stat = ({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: React.ReactNode;
  sub: string;
  color?: string;
}) => (
  <div styleName="hero-stat">
    <div styleName="kpi-label">{label}</div>
    <div styleName="kpi-value" style={color ? { color } : undefined}>
      {value}
    </div>
    <div styleName="kpi-caption">{sub}</div>
  </div>
);

const BuildingsTab = () => {
  const { summary, mode } = useContext(EventsReportContext);

  if (!summary) {
    return null;
  }

  const { buildings, processed } = summary;
  const isResolved = mode === 'resolved';
  const withIssues = buildings.filter(
    (building) => building.critical + building.major > 0,
  );
  const healthy = buildings.filter(
    (building) => building.critical + building.major + building.minor === 0,
  );
  const issuesTotal = buildings.reduce(
    (sum, building) => sum + building.critical + building.major,
    0,
  );
  const eventsTotal = buildings.reduce(
    (sum, building) => sum + building.total,
    0,
  );

  const columns: ColumnProps<DashboardBuilding>[] = [
    {
      title: 'Building',
      key: 'name',
      sorter: (left, right) => left.name.localeCompare(right.name),
      render: (_, building) => (
        <Link to={`/dashboard/buildings/${building.id}`}>{building.name}</Link>
      ),
    },
    {
      title: 'Region',
      key: 'region',
      sorter: (left, right) => left.region.localeCompare(right.region),
      render: (_, building) => building.region || '—',
    },
    {
      title: 'Critical + major',
      key: 'open',
      align: 'right',
      sorter: (left, right) =>
        left.critical + left.major - (right.critical + right.major),
      render: (_, building) => {
        const open = building.critical + building.major;
        return (
          <span styleName={open > 0 ? 'status area-red' : 'status area-green'}>
            {open}
          </span>
        );
      },
    },
    {
      title: isResolved ? 'Resolved events' : 'Open events',
      key: 'total',
      align: 'right',
      sorter: (left, right) => left.total - right.total,
      render: (_, building) => building.total.toLocaleString(),
    },
    {
      title: 'Severity mix',
      key: 'mix',
      width: 160,
      render: (_, building) => (
        <SeverityMixBar counts={building} total={building.total} />
      ),
    },
    {
      title: 'Processed · 30d',
      key: 'processed',
      align: 'right',
      sorter: (left, right) => left.processed - right.processed,
      render: (_, building) => (
        <span styleName={building.processed > 0 ? 'num' : 'num faint'}>
          {building.processed.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Processed vs open · 30d',
      key: 'trend',
      render: (_, building) => (
        <ProcessedTrend
          processed={building.processedTrend}
          open={building.openTrend}
        />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, building) => {
        const status = buildingStatus(building);
        return <span styleName={status.style}>{status.label}</span>;
      },
    },
  ];

  return (
    <div>
      <div styleName="hero">
        <div styleName="hero-stats">
          <Stat
            label="Buildings"
            value={buildings.length}
            sub={`${withIssues.length} with critical or major issues`}
          />
          <Stat
            label="Critical + major"
            value={issuesTotal}
            sub={isResolved ? 'resolved issues' : 'open issues'}
            color={!isResolved && issuesTotal > 0 ? '#ef4444' : undefined}
          />
          <Stat
            label="Healthy buildings"
            value={healthy.length}
            sub="no Minor or higher events"
            color="#22c55e"
          />
          <Stat
            label={isResolved ? 'Resolved events' : 'Open events'}
            value={eventsTotal.toLocaleString()}
            sub="all severities"
          />
          <Stat
            label="Events processed"
            value={processed.events.toLocaleString()}
            sub="last 30 days"
          />
        </div>
      </div>
      <IBTable
        size="small"
        columns={columns}
        dataSource={[...buildings].sort(bySeverity)}
        rowKey={(building) => building.id}
        scroll={{ x: 'max-content' }}
        pagination={{ defaultPageSize: 25, showSizeChanger: true }}
      />
      <div styleName="meta">
        Processed counts every event first seen in the last 30 days, open or
        resolved, all severities.{' '}
        {processed.unassigned > 0 &&
          `${processed.unassigned.toLocaleString()} event${processed.unassigned === 1 ? ' in that period is' : 's in that period are'} not assigned to a building and ${processed.unassigned === 1 ? 'is' : 'are'} not listed. `}
        Sorted by critical and major issues, then{' '}
        {isResolved ? 'resolved' : 'open'} events. Trend: <TrendLegend />
      </div>
    </div>
  );
};

export default BuildingsTab;
